import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { calculatePointOnArc } from "@/Helpers/calculatePointsOnArc";
import type { MutableRefObject } from "react";
import { hasValidLatitudeLongitude } from "./hasValidLatitudeLongitude";
import { latitudeLongitudeToNumbers } from "./latitudeLongitudeToNumbers";

function animateMarker(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation>,
  delivery: Partial<MapLocation>,
  position: number,
) {
  // A missing or (0,0) coordinate is "no point" — otherwise the animated
  // marker travels the arc out to the ocean off Africa (ENG-4342).
  const missingPickupPoint = !hasValidLatitudeLongitude(pickup);
  const missingDeliveryPoint = !hasValidLatitudeLongitude(delivery);

  if (missingPickupPoint || missingDeliveryPoint) {
    return;
  }

  const point = latitudeLongitudeToNumbers(
    calculatePointOnArc(
      pickup?.latitudeLongitude as Required<LatitudeLongitude>,
      delivery?.latitudeLongitude as Required<LatitudeLongitude>,
      position,
    ),
  );

  // getSource returns the union of every source type mapbox supports, and only
  // the GeoJSON one has setData. Narrowing on the method is enough and needs no
  // cast, where the previous @ts-ignore silenced the whole call.
  const markerSource = map.getSource("markerSource");
  if (markerSource && "setData" in markerSource) {
    markerSource.setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: "Point",
        coordinates: point,
      },
    });
  }
}

function arrowMarker(state: ShipmentState) {
  switch (state) {
    case ShipmentState.Quoted:
      return "animated-marker-pin";
    case ShipmentState.QuoteRequested:
      return "animated-marker-pin";
    case ShipmentState.BookingConfirmed:
      return "animated-marker-arrow";
    case ShipmentState.BookingRequested:
      return "animated-marker-arrow";
    case ShipmentState.InTransit:
      return "animated-marker-pin";
    case ShipmentState.Delivered:
      return "animated-marker-arrow-green";
    case ShipmentState.Cancelled:
      return "animated-marker-arrow";
    case ShipmentState.Lost:
      return "animated-marker-arrow";
    case ShipmentState.BookingFailed:
      return "animated-marker-pin";
    case ShipmentState.OnHold:
      return "animated-marker-pin";
  }
}

function calculateBearing(
  pickup: Partial<MapLocation> | undefined,
  delivery: Partial<MapLocation> | undefined,
  position: number,
) {
  const currentLoc = latitudeLongitudeToNumbers(
    calculatePointOnArc(
      pickup?.latitudeLongitude as Required<LatitudeLongitude>,
      delivery?.latitudeLongitude as Required<LatitudeLongitude>,
      position,
    ),
  );

  const locInFront = latitudeLongitudeToNumbers(
    calculatePointOnArc(
      pickup?.latitudeLongitude as Required<LatitudeLongitude>,
      delivery?.latitudeLongitude as Required<LatitudeLongitude>,
      position + 0.1,
    ),
  );

  /*
        
        
                        |
                        |
                        | deltaY
                        |
                        |
                        |
    -------------------
    deltaX

    So the angle from true north is (180-theta) - 90
    */

  const deltaX = locInFront[0] - currentLoc[0];
  const deltaY = locInFront[1] - currentLoc[1];

  // atan2 gives the angle from the positive x axis
  const atan2 = (180 / 3.14159) * Math.atan2(deltaY, deltaX);
  const bearing = 90 - atan2;

  return bearing;
}

function addMarker(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation>,
  delivery: Partial<MapLocation>,
  shipmentState: ShipmentState,
) {
  // A missing or (0,0) coordinate is "no point" — otherwise the animated
  // marker travels the arc out to the ocean off Africa (ENG-4342).
  const missingPickupPoint = !hasValidLatitudeLongitude(pickup);
  const missingDeliveryPoint = !hasValidLatitudeLongitude(delivery);

  if (missingPickupPoint || missingDeliveryPoint) {
    return;
  }

  const point = latitudeLongitudeToNumbers(
    calculatePointOnArc(
      pickup?.latitudeLongitude as Required<LatitudeLongitude>,
      delivery?.latitudeLongitude as Required<LatitudeLongitude>,
      0.5,
    ),
  );

  const bearing = calculateBearing(pickup, delivery, 0.5);

  map.addSource("markerSource", {
    type: "geojson",
    data: {
      type: "Feature",
      properties: {
        bearing: bearing,
      },
      geometry: {
        type: "Point",
        coordinates: point,
      },
    },
  });

  map.addLayer({
    id: "marker",
    source: "markerSource",
    type: "symbol",
    layout: {
      "icon-image": arrowMarker(shipmentState),
      "icon-rotate": ["get", "bearing"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });
}

function removeMarker(map: mapboxgl.Map) {
  if (map.getLayer("marker")) {
    map.removeLayer("marker");
  }

  if (map.getSource("markerSource")) {
    map.removeSource("markerSource");
  }
}

function shouldAnimate(state: ShipmentState) {
  switch (state) {
    case ShipmentState.Quoted:
      return true;
    case ShipmentState.QuoteRequested:
      return true;
    case ShipmentState.BookingConfirmed:
      return false;
    case ShipmentState.BookingRequested:
      return false;
    case ShipmentState.InTransit:
      return true;
    case ShipmentState.Delivered:
      return false;
    case ShipmentState.Cancelled:
      return false;
    case ShipmentState.Lost:
      return false;
    case ShipmentState.BookingFailed:
      return false;
    case ShipmentState.OnHold:
      return false;
  }
}

function stopAnimation(requestRef: MutableRefObject<number | undefined>) {
  if (requestRef.current !== undefined) {
    cancelAnimationFrame(requestRef.current);
  }
}

function startAnimation(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation>,
  delivery: Partial<MapLocation>,
  requestRef: MutableRefObject<number | undefined>,
) {
  // A missing or (0,0) coordinate is "no point" — otherwise the animated
  // marker travels the arc out to the ocean off Africa (ENG-4342).
  const missingPickupPoint = !hasValidLatitudeLongitude(pickup);
  const missingDeliveryPoint = !hasValidLatitudeLongitude(delivery);

  if (missingPickupPoint || missingDeliveryPoint) {
    return;
  }

  let position = 0;

  function animate() {
    position += 0.001;

    if (position > 1) {
      position = 0;
    }

    animateMarker(map, pickup, delivery, position);

    requestRef.current = requestAnimationFrame(animate);
  }

  animate();
}

export function setupMarker(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation>,
  delivery: Partial<MapLocation>,
  shipmentState: ShipmentState,
  requestRef: MutableRefObject<number | undefined>,
) {
  removeMarker(map);
  stopAnimation(requestRef);

  // A missing or (0,0) coordinate is "no point" — otherwise the animated
  // marker travels the arc out to the ocean off Africa (ENG-4342).
  const missingPickupPoint = !hasValidLatitudeLongitude(pickup);
  const missingDeliveryPoint = !hasValidLatitudeLongitude(delivery);

  if (missingPickupPoint || missingDeliveryPoint) {
    return;
  }

  addMarker(map, pickup, delivery, shipmentState);

  if (shouldAnimate(shipmentState)) {
    startAnimation(map, pickup, delivery, requestRef);
  }
}
